import { supabase } from '../lib/supabase';
import apiClient from './client';

export interface TicketPortalConfigResponse {
  success: boolean;
  data: {
    ticketPortalUrl: string;
    ticketDetailPath: string;
    updatedAt?: string;
    updatedBy?: string;
    updatedByName?: string;
    organizationId?: string;
  };
  error?: string;
}

export interface UpdateTicketPortalPayload {
  ticketPortalUrl: string;
  ticketDetailPath?: string;
  organizationId?: string;
}

const DEFAULT_PORTAL_CONFIG = {
  ticketPortalUrl: typeof window !== 'undefined' ? window.location.origin : 'https://compass.rezilyens.com',
  ticketDetailPath: '/tickets'
};

export const settingsApi = {
  getTicketPortalConfig: async (organizationId?: string): Promise<TicketPortalConfigResponse> => {
    if (supabase) {
      try {
        let query = supabase
          .from('configurations')
          .select('*')
          .eq('key', 'ticket_portal_settings');

        if (organizationId && organizationId !== 'ALL') {
          query = query.eq('organization_id', organizationId);
        }

        const { data, error } = await query.maybeSingle();
        if (!error && data?.value) {
          return {
            success: true,
            data: {
              ticketPortalUrl: data.value.ticketPortalUrl || DEFAULT_PORTAL_CONFIG.ticketPortalUrl,
              ticketDetailPath: data.value.ticketDetailPath || DEFAULT_PORTAL_CONFIG.ticketDetailPath,
              updatedAt: data.updated_at,
              organizationId: data.organization_id
            }
          };
        }
      } catch {
        // Fall back to default
      }

      return {
        success: true,
        data: {
          ...DEFAULT_PORTAL_CONFIG,
          organizationId
        }
      };
    }

    try {
      const { data } = await apiClient.get<TicketPortalConfigResponse>('/admin/settings/ticket-portal', {
        params: organizationId ? { organizationId } : undefined
      });
      return data;
    } catch {
      return {
        success: true,
        data: { ...DEFAULT_PORTAL_CONFIG, organizationId }
      };
    }
  },

  updateTicketPortalConfig: async (payload: UpdateTicketPortalPayload): Promise<TicketPortalConfigResponse> => {
    if (supabase) {
      const configValue = {
        ticketPortalUrl: payload.ticketPortalUrl,
        ticketDetailPath: payload.ticketDetailPath || '/tickets'
      };

      const { data, error } = await supabase
        .from('configurations')
        .upsert({
          organization_id: payload.organizationId || null,
          key: 'ticket_portal_settings',
          value: configValue,
          updated_at: new Date().toISOString()
        }, { onConflict: 'organization_id,key' })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          ticketPortalUrl: configValue.ticketPortalUrl,
          ticketDetailPath: configValue.ticketDetailPath,
          updatedAt: data?.updated_at,
          organizationId: payload.organizationId
        }
      };
    }

    const { data } = await apiClient.patch<TicketPortalConfigResponse>('/admin/settings/ticket-portal', payload);
    return data;
  },

  testTicketPortalUrl: async (payload: {
    ticketPortalUrl: string;
    ticketDetailPath?: string;
    sampleTicketNumber?: string;
  }): Promise<{ success: boolean; testUrl?: string; error?: string }> => {
    const baseUrl = (payload.ticketPortalUrl || '').replace(/\/$/, '');
    const path = (payload.ticketDetailPath || '/tickets').replace(/^\/?/, '/');
    const sample = payload.sampleTicketNumber || 'INC-1001';
    const testUrl = `${baseUrl}${path}/${sample}`;

    return {
      success: true,
      testUrl
    };
  }
};
